import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import {
  UPLOAD_CHARACTER_SUBPANEL_KEY,
  UPLOAD_CHARACTER_TAB,
} from 'components/CharacterPanel/constants';
import { webSocketStore } from 'stores';

export type PanelKey =
  | ''
  | 'start'
  | 'character'
  | 'history'
  | 'label'
  | 'advanced';

export type SessionIslandState = 'suggestions' | 'variations' | '';

export const PANEL_KEY_CHARACTER: PanelKey = 'character' as const;

export type PoseFrontDoorMode = 'project' | 'text' | 'image';

export type MotionFrontDoorMode =
  | 'project'
  | 'video'
  | 'text'
  | 'existing'
  | '';

type UiStoreState = {
  openPanel: PanelKey;
  promptFocus: boolean;
  subPanelKey: string; // should be unique among subpanels
  subPanelSectionKey: string; // use when subpanel has tabs or overlays
  hotkeyInfoOpen: boolean;
  poseFrontDoorMode: PoseFrontDoorMode;
  motionFrontDoorMode: MotionFrontDoorMode;
};

const defaultValues: UiStoreState = {
  openPanel: '',
  promptFocus: false,
  subPanelKey: '',
  subPanelSectionKey: '',
  hotkeyInfoOpen: false,
  poseFrontDoorMode: 'text',
  motionFrontDoorMode: 'video',
};

export const state = proxy<UiStoreState>(defaultValues);

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'UI', enabled: true });
}

///// Actions:
export function setOpenPanel(key: PanelKey) {
  state.openPanel = key;

  if (key === '') {
    setSubPanelKey('');
  }

  // force a resize event to update the layout
  // this is necessary because fiber/Canvas doesn't update and so drei/View doesn't update either
  setTimeout(() => {
    const elem = document.getElementById('scissor-canvas');
    if (elem) {
      elem.style.height = '119px';
      setTimeout(() => {
        elem.style.height = '';
      }, 50);
    }
  });
}

export function toggleOpenPanel(key: PanelKey) {
  if (state.openPanel === key) {
    setOpenPanel('');
  } else {
    setOpenPanel(key);
  }
}

export function setSubPanelKey(key: string) {
  state.subPanelKey = key;
}

export function setSubPanelSectionKey(key: string) {
  state.subPanelSectionKey = key;
}

export function setPoseFrontDoorMode(mode: PoseFrontDoorMode) {
  state.poseFrontDoorMode = mode;
}

export function setMotionFrontDoorMode(mode: MotionFrontDoorMode) {
  state.motionFrontDoorMode = mode;
}

export function highlightUploadError() {
  if (
    state.openPanel === PANEL_KEY_CHARACTER &&
    state.subPanelKey === UPLOAD_CHARACTER_SUBPANEL_KEY
  ) {
    setSubPanelSectionKey(UPLOAD_CHARACTER_TAB);
  }
}

export function setPromptFocus(value: boolean) {
  state.promptFocus = value;
}

export function toggleHotkeyInfoOpen() {
  state.hotkeyInfoOpen = !state.hotkeyInfoOpen;
}

//// WEBSOCKET EVENTS

webSocketStore.on('UPLOAD_FAILED', () => {
  highlightUploadError();
});
